// The go-live control (Module 02 §4 S1 + S8, §3.6).
//
// Two-stage by design: the button is disabled until the client-side mirror of
// the gate is clear, with a tooltip AND an inline list saying exactly what is
// outstanding ("disabled with tooltip listing unmet blocking items", §4 S1);
// pressing it asks for confirmation, because going live is a one-way door.
//
// The client mirror is NOT the gate. The server re-evaluates the whole checklist
// inside the per-tenant wizard advisory lock and re-checks it again in the
// transition transaction, so anything this component believes can be overruled —
// the refusal is rendered from the server's own 422, never guessed.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { components } from "@assessly/api-client";
import { Button } from "../../../components/button";
import { useGoLive } from "../hooks/onboarding-queries";
import { useOnboardingPermissions } from "../hooks/permissions";
import { checklistItemLabelKey, goLiveFailure } from "../lib/wizard-problems";
import { GO_LIVE_SCORE_THRESHOLD, goLiveBlockers } from "../lib/wizard-steps";
import { WizardDialog } from "./wizard-dialog";
import { WizardProblemNotice } from "./wizard-notices";

type S = components["schemas"];

export interface GoLiveButtonProps {
  session: S["OnboardingSession"];
  steps: readonly S["OnboardingStepState"][];
  summary: S["OnboardingChecklistSummary"];
  /** ONE primary per view (§3.10): the checklist owns go-live as its primary;
   * the home renders it secondary beside its resume CTA. */
  primary?: boolean;
}

export function GoLiveButton({ session, steps, summary, primary = true }: GoLiveButtonProps) {
  const { t } = useTranslation();
  const perms = useOnboardingPermissions();
  const goLive = useGoLive();
  const [confirming, setConfirming] = useState(false);

  const blockers = goLiveBlockers({
    session,
    steps,
    summary,
    canGoLive: perms.has("onboarding.go_live"),
  });

  // Every reason the button is off, in the tooltip AND in the panel below it —
  // a tooltip alone is unusable on touch.
  const reasons: string[] = [];
  if (blockers.alreadyLive) reasons.push(t("onboarding.goLive.alreadyLive"));
  if (blockers.notPermitted) reasons.push(t("onboarding.goLive.blockedPermission"));
  if (blockers.incompleteSteps.length > 0) {
    reasons.push(
      t("onboarding.goLive.blockedSteps", { steps: blockers.incompleteSteps.join(", ") }),
    );
  }
  if (blockers.scoreGap !== null) {
    reasons.push(
      t("onboarding.goLive.blockedScore", {
        score: summary.score,
        required: GO_LIVE_SCORE_THRESHOLD,
        gap: blockers.scoreGap,
      }),
    );
  }

  const failure = goLiveFailure(goLive.error);
  const succeeded = goLive.isSuccess;

  if (succeeded) {
    return (
      <div className="state state-empty wizard-notice" role="status" data-testid="go-live-done">
        <p className="state-title">{t("onboarding.goLive.doneTitle")}</p>
        <p className="state-body">
          {t("onboarding.goLive.doneBody", { score: goLive.data.score })}
        </p>
      </div>
    );
  }

  return (
    <div className="go-live" data-testid="go-live">
      <Button
        variant={primary ? "primary" : "secondary"}
        className="go-live-button"
        busy={goLive.isPending}
        disabled={!blockers.ok}
        title={reasons.length > 0 ? reasons.join(" ") : undefined}
        onClick={() => {
          setConfirming(true);
        }}
      >
        {goLive.isPending ? t("onboarding.goLive.working") : t("onboarding.goLive.cta")}
      </Button>

      {reasons.length > 0 || blockers.blockingUnmet.length > 0 ? (
        <div className="go-live-blockers" data-testid="go-live-blockers">
          <p className="state-title">{t("onboarding.goLive.blockedTitle")}</p>
          {reasons.map((reason) => (
            <p className="state-detail" key={reason}>
              {reason}
            </p>
          ))}
          {blockers.blockingUnmet.length > 0 ? (
            <>
              <p className="state-detail">{t("onboarding.goLive.blockedItems")}</p>
              <ul className="wizard-notice-list">
                {blockers.blockingUnmet.map((itemKey) => (
                  <li key={itemKey}>
                    {t(checklistItemLabelKey(itemKey), { defaultValue: itemKey })}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      ) : null}

      {/* The single irreversible action in the product. It gets the modal that
          moves focus, traps Tab, closes on Escape and announces itself — a
          keyboard or screen-reader user was previously left behind an
          unannounced div. */}
      {confirming ? (
        <WizardDialog
          testId="go-live-confirm"
          destructive
          title={t("onboarding.goLive.confirmTitle")}
          confirmLabel={t("onboarding.goLive.confirm")}
          cancelLabel={t("onboarding.goLive.cancel")}
          busy={goLive.isPending}
          onConfirm={() => {
            setConfirming(false);
            goLive.mutate();
          }}
          onCancel={() => {
            setConfirming(false);
          }}
        >
          <p>{t("onboarding.goLive.confirmBody")}</p>
        </WizardDialog>
      ) : null}

      {goLive.error !== null ? (
        <div className="go-live-failure">
          {failure !== undefined && failure.blockingUnmet.length > 0 ? (
            <>
              <p className="state-title">{t("onboarding.goLive.failedTitle")}</p>
              <p className="state-detail">{t("onboarding.goLive.blockedItems")}</p>
              <ul className="wizard-notice-list">
                {failure.blockingUnmet.map((itemKey) => (
                  <li key={itemKey}>
                    {t(checklistItemLabelKey(itemKey), { defaultValue: itemKey })}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <WizardProblemNotice
              error={goLive.error}
              onRetry={() => {
                goLive.reset();
              }}
              titleKey="onboarding.goLive.failedTitle"
            />
          )}
        </div>
      ) : null}
    </div>
  );
}
